import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { THEMES, DEFAULT_THEME_ID, themeToCssVars, type SympheniaTheme } from './themes';

// ── DOM-Hilfsfunktionen ─────────────────────────────────────────
//
// Dieser Block steht BEWUSST vor dem create()-Aufruf.
// Der persist-`merge`-Callback läuft synchron während der Store-Erzeugung
// und ruft applyThemeToDom() auf. Stünde `SNAP_KEY` — wie
// zuvor — weiter unten, läge er zu diesem Zeitpunkt noch in der Temporal
// Dead Zone: applyThemeToDom bräche mit einem ReferenceError ab. Zustand
// verschluckt Fehler aus `merge` stillschweigend und fällt auf den
// Default-State zurück — das gespeicherte Theme wäre bei jedem App-Start weg.

/** Minimaler Snapshot für das Inline-Script in index.html (FOUC-Prävention) */
const SNAP_KEY = 'symphenia-theme-snap';

function writeThemeSnapshot(theme: SympheniaTheme, accentHex: string | null) {
  try {
    localStorage.setItem(
      SNAP_KEY,
      JSON.stringify({
        bg:        theme.background,
        fg:        theme.foreground,
        sidebar:   theme.sidebarBg,
        isDark:    theme.isDark,
        accent:    accentHex,
      }),
    );
  } catch {
    // localStorage nicht verfügbar → ignorieren
  }
}

/**
 * Zuletzt bekannte Mandanten-Primärfarbe aus dem Snapshot.
 *
 * Die Farbe kommt vom Server und steht erst nach `GET /tenants/me` fest. Ohne
 * diesen Zwischenspeicher zeigte jeder Seitenaufruf zuerst die Theme-Akzente
 * und spränge dann auf die Firmenfarbe — bei jedem Laden sichtbar.
 */
function readCachedAccent(): string | null {
  try {
    const raw = localStorage.getItem(SNAP_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as { accent?: string | null };
    return snap.accent ?? null;
  } catch {
    return null;
  }
}

/** Wandelt "#0ea5e9" in die HSL-Komponenten "199 89% 49%" (Tailwind-Variablen). */
function hexToHsl(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!result) return null;

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Eine Schrift wird nie von einem fremden Host geladen (Vertragsprüfung 10.10.2026,
// A-7): Bis dahin hängte ein `ensureFontLoaded` für jede Schrift außerhalb einer
// Liste ein Stylesheet von fonts.googleapis.com in den Kopf — die IP-Adresse des
// Benutzers ging an Google. `fontSans` ist nur ein Stapel von Namen; was davon am
// Gerät nicht liegt, überspringt der Browser. Wer eine eigene Schrift will, legt
// die Datei ins Projekt (`@font-face`, eigener Host). `themes.test.ts` hält das fest.

/**
 * Wendet Theme und — falls gesetzt — die Mandanten-Primärfarbe auf :root an.
 *
 * Die Firmenfarbe überlagert im ERP **immer** die Akzente des Themes. Anders als
 * in der PWA, wo jeder Mitarbeiter im eigenen Profil eine Palette wählt, ist das
 * Theme hier selbst eine Mandanten-Einstellung (`TenantSettingsPage`) — beide
 * stehen auf derselben Karte. Würde die Themewahl die Farbe verdrängen, bliebe
 * die eben gesetzte Firmenfarbe unsichtbar.
 *
 * Akzent im selben Schreibvorgang, nicht nachträglich: zwei Schreiber auf :root
 * sind reihenfolgeabhängig und lassen sich nicht zurücknehmen.
 */
function applyThemeToDom(theme: SympheniaTheme, accentHex: string | null) {
  // Kein automatisches Dark-Mode-Overlay: wer Dark Mode will, wählt
  // ein dunkles Theme (z. B. Midnight) explizit aus.
  const vars = themeToCssVars(theme);

  if (accentHex) {
    const hsl = hexToHsl(accentHex);
    if (hsl) {
      vars['--primary'] = hsl;
      vars['--accent'] = hsl;
      vars['--ring'] = hsl;
      vars['--sidebar-active-bg'] = accentHex;
    }
  }

  const root = document.documentElement;
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
  root.classList.toggle('dark', theme.isDark);
  writeThemeSnapshot(theme, accentHex);
}

// ── Store ───────────────────────────────────────────────────────

interface ThemeState {
  activeThemeId: string;
  theme: SympheniaTheme;
  /** Mandanten-Primärfarbe (Hex) oder null — überlagert die Akzente des Themes */
  tenantAccent: string | null;

  /** Wechselt zu einem vorgefertigten Theme */
  setTheme: (themeId: string) => void;

  /** Überschreibt einzelne Token des aktiven Themes (Custom-Design) */
  patchTheme: (patch: Partial<SympheniaTheme>) => void;

  /** Wendet die CSS-Variablen des aktiven Themes auf :root an */
  applyTheme: () => void;

  /**
   * Setzt die Mandanten-Primärfarbe und wendet sie sofort an.
   * `null` nimmt sie zurück — die Akzente des Themes gelten wieder.
   */
  setTenantAccent: (hex: string | null) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      activeThemeId: DEFAULT_THEME_ID,
      theme: THEMES[DEFAULT_THEME_ID],
      // Aus dem Snapshot, damit die Firmenfarbe schon vor der Server-Antwort steht
      tenantAccent: readCachedAccent(),

      setTheme: (themeId: string) => {
        const theme = THEMES[themeId] ?? THEMES[DEFAULT_THEME_ID];
        set({ activeThemeId: themeId, theme });
        applyThemeToDom(theme, get().tenantAccent); // handhabt .dark-Klasse intern korrekt
      },

      patchTheme: (patch: Partial<SympheniaTheme>) => {
        const patched = { ...get().theme, ...patch };
        set({ theme: patched });
        applyThemeToDom(patched, get().tenantAccent);
      },

      applyTheme: () => {
        const { theme, tenantAccent } = get();
        applyThemeToDom(theme, tenantAccent); // handhabt .dark-Klasse intern korrekt
      },

      setTenantAccent: (hex: string | null) => {
        set({ tenantAccent: hex });
        applyThemeToDom(get().theme, hex);
      },
    }),
    {
      name: 'symphenia-theme',
      // Nur die ID persistieren; Theme-Objekt wird beim Hydrate neu aufgelöst.
      // tenantAccent gehört bewusst NICHT hierher — er kommt vom Server und
      // wird über den Theme-Snapshot zwischengespeichert.
      partialize: (state) => ({ activeThemeId: state.activeThemeId }),
      // Nach Hydration CSS-Vars anwenden.
      // `persisted` ist beim allerersten Start (leerer Storage) undefined —
      // daher der optionale Zugriff: ein Fehler hier würde von zustand still
      // verschluckt und der Store fiele auf den Default zurück.
      merge: (persisted, current) => {
        const p = persisted as { activeThemeId?: string } | undefined;
        const themeId = p?.activeThemeId ?? DEFAULT_THEME_ID;
        const theme = THEMES[themeId] ?? THEMES[DEFAULT_THEME_ID];
        // DOM sofort aktualisieren (handhabt .dark-Klasse intern)
        applyThemeToDom(theme, current.tenantAccent);
        return { ...current, activeThemeId: themeId, theme };
      },
    },
  ),
);
